import type { Student } from "./student";
import type { FrequentationTagAssignmentDto } from "./frequentation-tag";
import type { ModuleProgressInfo } from "./learning-modules";
import type { Planning } from "./planning";
import type { Teacher } from "./teacher";

export enum FrequentationStatus {
  EnCours = "EnCours",
  PauseSuspendu = "PauseSuspendu",
  Complete = "Complete",
  Abandonne = "Abandonne",
}

import type { LearningProgram } from "./learning-program";

export interface FrequentationGroup {
  id: string;
  name: string;
}

export interface Frequentation {
  id: string;
  studentId: string;
  student?: Student;
  learningProgramId: string;
  learningProgram?: LearningProgram;
  startDate: string;
  endDate?: string | null;
  status: FrequentationStatus;
  planningId?: string | null;
  /** Lightweight planning snapshot (without module sequences), also used by the student record. */
  planning?: Planning | null;
  /**
   * Calendar v3 (#592): the personalized schedule chain this frequentation carries,
   * null when the student follows their delivery's schedule. Managed on the server by the
   * personalization endpoints — never rewritten through the frequentation CRUD.
   */
  scheduleAssignmentChainId?: string | null;
  isIndividual?: boolean;
  tags?: FrequentationTagAssignmentDto[];
  groups?: FrequentationGroup[];
  fundingSource?: string | null;
  pathType?: string | null;
  exemptionCode?: string | null;
  isConcomitant?: boolean;
  isSmallGroup?: boolean;
  nbHoursFG?: number | null;
  /** The student's usual exam venue (#823): seeds new exams, each one overridable. */
  defaultExamLocationId?: string | null;
  /** The second staff member beside the enseignant-tuteur (#824). */
  secondaryStaffId?: string | null;
  /** Included by detail/student-record endpoints, including for historical archived staff. */
  secondaryStaff?: Teacher | null;
  secondaryStaffRole?: string | null;
  noteCount?: number;
  /** Presence counts by PresenceStatus (e.g. Present, JustifiedAbsence, …). Only populated by frequentationsByStudent. */
  presenceSummary?: Record<string, number>;
  /** Presence counts by PresenceType (e.g. Normal, Exam, Remediation, …). Only populated by frequentationsByStudent. */
  presenceTypeSummary?: Record<string, number>;
  /** Per-module progress for all program modules. Only populated by frequentationsByStudent. */
  moduleProgress?: ModuleProgressInfo[];
}

/** Outcome of a GRICS frequentation import run (see `POST /api/GricsImport/Frequentations`). */
export interface GricsFrequentationImportResult {
  scanned: number;
  created: number;
  updated: number;
  unchanged: number;
  errors: string[];
}
